import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {useEffect, useRef, useState} from '@wordpress/element';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {type TokenResult, type Payments, type ApplePay, type PaymentRequest} from '@square/web-payments-sdk-types';
import {z} from '@peachpay/checkout-blocks';
import {getErrorString} from '../../../../../../frontend/@shared/ts/error';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {buildOptions} from '../payment-request';

const SettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	fallback_currency: z.string().nullable(),
	supports: z.string().array(),
	square: z.object({
		application_id: z.string(),
		location_id: z.string(),
		country_code: z.string(),
	}),
});

const parsedResult = SettingsSchema.safeParse(getSetting('peachpay_square_applepay_data'));
if (!parsedResult.success) {
	throw new Error(`Payment method "peachpay_square_applepay" setup aborted. Invalid settings.\n\n${parsedResult.error.message}`);
}

const settings = parsedResult.data;
const title = decodeEntities(settings.title);
const description = decodeEntities(settings.description);

const iconStyle = {maxHeight: '25px'} as const;
const descriptionStyle = {textAlign: 'left' as const, margin: '0.5rem 0 0'} as const;
const hrStyle = {margin: '0.5rem 0'} as const;
const smallTextStyle = {textAlign: 'left' as const, margin: '0', fontSize: 'smaller'} as const;

if (!window.Square) {
	throw new Error('Payment method "peachpay_square_applepay" setup aborted. Square SDK not loaded.');
}

let payments: Payments;
try {
	payments = window.Square.payments(settings.square.application_id, settings.square.location_id);
} catch (err) {
	throw new Error(`Payment method "peachpay_square_applepay" setup aborted. Could not create Payments instance.\n\n${getErrorString(err)}`);
}

function Label(props: PaymentMethodInterface) {
	const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
	return (
		<>
			<PaymentMethodLabel text={title} />
			<PaymentMethodIcons className='pp-label-icon' icons={[{
				id: 'peachpay_square_applepay',
				src: settings.icon,
				alt: title,
			}]} />
		</>
	);
}

function Content({billing, cartData, eventRegistration, emitResponse}: PaymentMethodInterface) {
	const {
		onPaymentSetup,
		onCheckoutFail,
	} = eventRegistration;

	const paymentRequestRef = useRef<PaymentRequest | null>(null);
	const applePayRef = useRef<ApplePay | null>(null);
	const [initError, setInitError] = useState<string | null>(null);

	useEffect(() => {
		const setupApplePay = async () => {
			paymentRequestRef.current = payments.paymentRequest(buildOptions({
				merchantCountry: settings.square.country_code,
				currency: billing.currency.code,
				items: cartData.cartItems,
				summary: billing.cartTotalItems,
				total: billing.cartTotal,
				displayPricesIncludingTax: billing.displayPricesIncludingTax,
			}));

			applePayRef.current = await payments.applePay(paymentRequestRef.current);
		};

		setupApplePay()
			.catch((err: unknown) => {
				const message = err instanceof Error ? err.message : __('Apple Pay initialization failed.', 'peachpay-for-woocommerce');
				setInitError(message);
			});

		return () => {
			applePayRef.current?.destroy()
				.catch(console.error);
		};
	}, []);

	useEffect(() => {
		paymentRequestRef.current?.update(buildOptions({
			merchantCountry: settings.square.country_code,
			currency: billing.currency.code,
			items: cartData.cartItems,
			summary: billing.cartTotalItems,
			total: billing.cartTotal,
			displayPricesIncludingTax: billing.displayPricesIncludingTax,
		}));
	}, [
		cartData.cartItems,
		billing.currency.code,
		billing.cartTotalItems,
		billing.cartTotal.label,
		billing.cartTotal.value,
		billing.displayPricesIncludingTax,
	]);

	useEffect(() => {
		const unsubscribe = onPaymentSetup(async () => {
			if (!applePayRef.current) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('Missing ref to Square Apple Pay element. Please try again or use a different payment method.', 'peachpay-for-woocommerce'),
				};
			}

			let tokenResult: TokenResult | null = null;
			try {
				tokenResult = await applePayRef.current.tokenize();
				if (tokenResult.status !== 'OK' || !tokenResult.token) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message: tokenResult.status === 'Cancel'
							? __('Payment was cancelled', 'peachpay-for-woocommerce')
							: ((tokenResult.errors ?? []) as Array<{message: string}>).map(err => err.message).join('<br/>')
								|| __('Apple Pay tokenization failed. Please try again or use a different payment method.', 'peachpay-for-woocommerce'),
					};
				}
			} catch (err) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: getErrorString(err),
				};
			}

			return {
				type: emitResponse.responseTypes.SUCCESS,
				meta: {
					paymentMethodData: {
						peachpay_square_source_id: tokenResult.token,
					},
				},
			};
		});
		return unsubscribe;
	}, [onPaymentSetup]);

	useEffect(() => {
		const unsubscribe = onCheckoutFail(async (data: CheckoutAfterProcessingWithErrorEventData) => ({
			type: emitResponse.responseTypes.ERROR,
			message: (data.processingResponse?.paymentDetails?.['message']) ?? __('An unknown error occured while processing the payment.', 'peachpay-for-woocommerce'),
			messageContext: emitResponse.noticeContexts.PAYMENTS,
		}));
		return unsubscribe;
	}, [onCheckoutFail]);

	if (settings.fallback_currency) {
		return <CurrencyFallback name={title} currency={settings.fallback_currency} />;
	}

	if (initError) {
		return <p>{initError}</p>;
	}

	return (
		<>
			<img style={iconStyle} src={settings.icon} alt=''></img>
			<p style={descriptionStyle}>{title} {__('selected for checkout.', 'peachpay-for-woocommerce')}</p>
			<hr style={hrStyle}/>
			<p style={smallTextStyle}>{description}</p>
		</>
	);
}

registerPaymentMethod({
	name: 'peachpay_square_applepay',
	ariaLabel: title,
	canMakePayment(data) {
		return data.paymentMethods.includes('peachpay_square_applepay');
	},
	// @ts-expect-error WC will clone these components and pass the props when used.
	label: <Label />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	content: <Content />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	edit: <Content />,
	supports: {
		features: settings.supports,
	},
});
